import { useState } from 'react'
import { ChevronsLeftRight } from 'lucide-react'

interface BeforeAfterSliderProps {
  beforeSrc: string
  afterSrc: string
  beforeAlt: string
  afterAlt: string
  beforeLabel?: string
  afterLabel?: string
  /** Intrinsic dimensions, used for the aspect ratio + img sizing. */
  width?: number
  height?: number
}

/**
 * Draggable before/after image comparison. The "after" is the base layer; the
 * "before" is clipped over it and revealed by a draggable divider. An invisible
 * full-bleed range input drives the position, so it works with pointer, touch,
 * and keyboard (arrow keys) and is announced to screen readers.
 */
export function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeAlt,
  afterAlt,
  beforeLabel = 'Before',
  afterLabel = 'After',
  width = 1600,
  height = 1000,
}: BeforeAfterSliderProps) {
  const [pos, setPos] = useState(50)

  return (
    <div
      className="not-prose border-border relative w-full touch-none overflow-hidden rounded-lg border select-none"
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {/* After — base layer */}
      <img
        src={afterSrc}
        alt={afterAlt}
        width={width}
        height={height}
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      <span className="absolute top-3 right-3 rounded bg-black/65 px-2 py-1 text-xs font-medium text-white">
        {afterLabel}
      </span>

      {/* Before — clipped overlay (full-size img, clipped so it stays aligned) */}
      <img
        src={beforeSrc}
        alt={beforeAlt}
        width={width}
        height={height}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable={false}
      />
      <span
        className="absolute top-3 left-3 rounded bg-black/65 px-2 py-1 text-xs font-medium text-white transition-opacity"
        style={{ opacity: pos > 14 ? 1 : 0 }}
      >
        {beforeLabel}
      </span>

      {/* Divider + grab handle (decorative; the range input drives it) */}
      <div
        className="pointer-events-none absolute inset-y-0 z-10 w-0.5 -translate-x-1/2 bg-white/90"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-neutral-800 shadow-lg">
          <ChevronsLeftRight className="h-5 w-5" />
        </div>
      </div>

      {/* Invisible control: pointer drag + keyboard, full bleed */}
      <input
        type="range"
        min={0}
        max={100}
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label={`Slide to compare the ${beforeLabel} and ${afterLabel} versions of the site`}
        className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
      />
    </div>
  )
}
